import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth, API } from "../App";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import axios from "axios";
import { useIsWebView } from "../hooks/useIsWebView";

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAuth } = useAuth();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) { setStatus("error"); setMessage(t("pay_no_session")); return; }

    let cancelled = false;
    const poll = async (attempt = 0) => {
      if (attempt >= 5 || cancelled) {
        if (!cancelled) { setStatus("error"); setMessage(t("pay_timeout")); }
        return;
      }
      setAttempts(attempt + 1);
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
        if (cancelled) return;
        if (response.data.payment_status === "paid") {
          setStatus("success"); setMessage(t("pay_success")); await checkAuth(); return;
        } else if (response.data.status === "expired") {
          setStatus("error"); setMessage(t("pay_expired")); return;
        }
        setTimeout(() => poll(attempt + 1), 2000);
      } catch { if (!cancelled) setTimeout(() => poll(attempt + 1), 2000); }
    };
    poll();
    return () => { cancelled = true; };
  }, [searchParams, checkAuth, t]); // eslint-disable-line react-hooks/exhaustive-deps -- poll runs once

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        {status === "checking" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#002FA7]/10 flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#002FA7] animate-spin" /></div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-2">{t("pay_checking")}</h1>
            <p className="text-[#6B7280] mb-4">{t("pay_checking_sub")}</p>
            <p className="text-[#6B7280]/60 text-sm">{t("pay_attempt", { n: attempts })}</p>
          </div>
        )}
        {status === "success" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#10B981]/10 flex items-center justify-center"><CheckCircle2 className="w-10 h-10 text-[#10B981]" /></div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-2">{t("pay_success")}</h1>
            <p className="text-[#6B7280] mb-8">{message}</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-full px-8 py-6" data-testid="go-dashboard-btn">{t("pay_go_dashboard")}</Button>
          </div>
        )}
        {status === "error" && (
          <div className="animate-fade-in">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#EF4444]/10 flex items-center justify-center"><XCircle className="w-10 h-10 text-[#EF4444]" /></div>
            <h1 className="font-['Outfit'] text-2xl font-semibold text-[#111111] mb-2">{t("pay_error")}</h1>
            <p className="text-[#6B7280] mb-8">{message}</p>
            <div className="flex flex-col gap-3">
              {!isWebView && <Button onClick={() => navigate("/pricing")} className="bg-[#002FA7] hover:bg-[#001d6a] text-white font-medium rounded-full px-8 py-6" data-testid="retry-btn">{t("pay_retry")}</Button>}
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="border-[#E5E5EA] text-[#111111] font-medium rounded-full px-8 py-6" data-testid="go-dashboard-error-btn">{t("pay_back_dashboard")}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
